// src/sectors/pilates/settings/tabs/sections/company/CompanyIdentitySection.jsx
import React from 'react';
import PropTypes from 'prop-types';

import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import { SectionCard } from '../../ui/SettingsUi';

export function CompanyIdentitySection({
  draft,
  dirty,
  patch,
  loading,
  error,
  companyEmails,
  onSave,
  onResetDraft,
}) {
  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    // loading bittiğinde editMode'u otomatik açma/kapama yok
  }, [loading]);

  const disabled = loading || saving;

  const handleEdit = () => setEditMode(true);

  const handleCancel = () => {
    onResetDraft?.();
    setEditMode(false);
  };

  const handleSave = async () => {
    if (!onSave) return;
    try {
      setSaving(true);
      await onSave(draft); // ✅ parent PATCH /me/identity yapacak
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      icon="store"
      title="İşletme Kimliği"
      subtitle="Ad ve iletişim bilgileri"
      right={
        loading || saving ? (
          <MDBox sx={{ display: 'grid', placeItems: 'center', width: 28, height: 28 }}>
            <CircularProgress size={18} />
          </MDBox>
        ) : (
          <Chip
            size="small"
            variant="outlined"
            label={dirty ? 'Değişiklik var' : 'Kaydedildi'}
            sx={{ borderRadius: '999px', fontWeight: 900 }}
          />
        )
      }
    >
      {error ? (
        <MDTypography variant="caption" sx={{ color: 'error.main', fontWeight: 900, mb: 1 }}>
          {error}
        </MDTypography>
      ) : null}

      {/* ✅ action buttons */}
      <MDBox sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.8, mb: 1 }}>
        {!editMode ? (
          <MDButton
            variant="outlined"
            color="dark"
            onClick={handleEdit}
            disabled={disabled}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900, height: 40 }}
          >
            Düzenle
          </MDButton>
        ) : (
          <>
            <MDButton
              variant="outlined"
              color="dark"
              onClick={handleCancel}
              disabled={disabled}
              sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900, height: 40 }}
            >
              Vazgeç
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSave}
              disabled={disabled || !dirty}
              sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900, height: 40 }}
            >
              Kaydet
            </MDButton>
          </>
        )}
      </MDBox>

      <MDBox sx={{ display: 'grid', gap: 1.25 }}>
        <MDInput
          label="İşletme Adı"
          value={draft.name || ''}
          onChange={(e) => patch('name', e.target.value)}
          disabled={!editMode || disabled}
        />

        <MDInput
          label="Telefon"
          value={draft.phone || ''}
          onChange={(e) => patch('phone', e.target.value)}
          disabled={!editMode || disabled}
        />

        <MDInput
          label="E-posta"
          value={draft.email || ''}
          onChange={(e) => patch('email', e.target.value)}
          disabled={!editMode || disabled}
        />

        <Divider sx={{ my: 1 }} />

        <MDBox>
          <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900 }}>
            Şirket E-postaları (Sahip & Admin)
          </MDTypography>

          {Array.isArray(companyEmails) && companyEmails.length ? (
            <MDBox sx={{ mt: 0.7, display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
              {companyEmails.map((m) => (
                <Chip
                  key={m}
                  size="small"
                  label={m}
                  variant="outlined"
                  sx={{ borderRadius: '999px', fontWeight: 900 }}
                />
              ))}
            </MDBox>
          ) : (
            <MDTypography variant="button" sx={{ mt: 0.5, opacity: 0.7, fontWeight: 800 }}>
              Şirket e-postası bulunamadı.
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </SectionCard>
  );
}

CompanyIdentitySection.propTypes = {
  draft: PropTypes.object.isRequired,
  dirty: PropTypes.bool.isRequired,
  patch: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  companyEmails: PropTypes.array,
  onSave: PropTypes.func, // ✅
  onResetDraft: PropTypes.func, // ✅
};
